import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Package, AlertCircle, Loader, ArrowRight, ExternalLink } from 'lucide-react';
import { getOrdersByIdentifier } from '@/service/customerServices';
import { Order } from '@/types';
import { useNavigate } from 'react-router-dom';
import ProtegaLogo from "@/assets/desktopLogo.png";

const TrackOrders: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim()) return;

    setIsLoading(true);
    try {
      const data = await getOrdersByIdentifier(identifier.trim());
      setOrders(data);
      setHasSearched(true);
    } catch (err) {
      console.error(err);
      setOrders([]);
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tw-min-h-screen tw-bg-[#1a1a1a] tw-text-gray-100 tw-font-sans">
      {/* Header */}
      <header className="tw-py-8 tw-px-6 tw-flex tw-justify-center tw-items-center">
        <img src={ProtegaLogo} alt="Protega" className="tw-h-10 tw-w-auto" />
      </header>

      <main className="tw-max-w-4xl tw-mx-auto tw-p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="tw-text-center tw-mb-12"
        >
          <h1 className="tw-text-4xl tw-font-bold tw-mb-4 tw-tracking-tight">Track Your Orders</h1>
          <p className="tw-text-gray-400 tw-text-lg">Enter your registered email or phone number to view your order history.</p>
        </motion.div>

        {/* Search Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSearch}
          className="tw-relative tw-max-w-xl tw-mx-auto tw-mb-16"
        >
          <div className="tw-relative">
            <Search className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500 tw-w-5 tw-h-5" />
            <input
              type="text"
              placeholder="Email or Mobile Number"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="tw-w-full tw-bg-[#252525] tw-border tw-border-gray-700 tw-rounded-2xl tw-py-5 tw-pl-12 tw-pr-32 tw-text-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#00C5FB] focus:tw-border-transparent tw-transition-all"
            />
            <button
              type="submit"
              disabled={isLoading || !identifier.trim()}
              className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-bg-[#00C5FB] tw-text-gray-900 tw-font-bold tw-px-6 tw-py-3 tw-rounded-xl hover:tw-bg-white tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {isLoading ? <Loader className="tw-animate-spin tw-w-5 tw-h-5" /> : "Search"}
            </button>
          </div>
        </motion.form>

        {/* Results Area */}
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="tw-flex tw-justify-center tw-py-20"
            >
              <div className="tw-w-12 tw-h-12 tw-border-4 tw-border-[#00C5FB] tw-border-t-transparent tw-rounded-full tw-animate-spin" />
            </motion.div>
          ) : hasSearched ? (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tw-space-y-6"
            >
              {orders.length > 0 ? (
                <div className="tw-grid tw-grid-cols-1 tw-gap-6">
                  {orders.map((order, idx) => (
                    <motion.div
                      key={`${order.order_id}-${idx}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="tw-bg-[#252525] tw-border tw-border-gray-700 tw-rounded-2xl tw-p-6 tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-gap-6"
                    >
                      <div className="tw-w-24 tw-h-24 tw-bg-gray-800 tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                        {order.product_image ? (
                          <img src={order.product_image} alt={order.product_title} className="tw-w-full tw-h-full tw-object-cover" />
                        ) : (
                          <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                            <Package className="tw-text-gray-600 tw-w-10 tw-h-10" />
                          </div>
                        )}
                      </div>

                      <div className="tw-flex-1 tw-text-center md:tw-text-left">
                        <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-gap-2 tw-mb-2">
                          <span className="tw-bg-gray-800 tw-text-gray-400 tw-text-[10px] tw-uppercase tw-tracking-widest tw-px-2 tw-py-1 tw-rounded-md tw-w-max tw-mx-auto md:tw-mx-0">
                            ORDER #{order.human_readable_order_id || order.order_id}
                          </span>
                          <span className="tw-text-gray-500 tw-text-sm">
                            {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <h3 className="tw-text-xl tw-font-bold tw-text-white tw-mb-1">{order.product_title}</h3>
                        <p className="tw-text-[#00C5FB] tw-font-semibold">₹{order.product_price}</p>
                      </div>

                      <div className="tw-flex tw-flex-col tw-items-center md:tw-items-end tw-gap-2 tw-text-sm">
                        {order.fulfillment_status && (
                          <div className="tw-flex tw-gap-2">
                            <span className="tw-text-gray-500">Status:</span>
                            <span className="tw-text-gray-300 tw-capitalize">{order.fulfillment_status}</span>
                          </div>
                        )}
                        {order.delivered_at && (
                          <div className="tw-flex tw-gap-2">
                            <span className="tw-text-gray-500">Delivered:</span>
                            <span className="tw-text-gray-300">
                              {new Date(order.delivered_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        )}
                        {order.return_status && order.return_status !== 'false' && (
                          <div className="tw-flex tw-flex-col tw-gap-1">
                            <div className="tw-flex tw-gap-2">
                              <span className="tw-text-gray-500">Return:</span>
                              <span className="tw-text-orange-400 tw-capitalize">{order.return_status.toString().replace('_', ' ')}</span>
                            </div>
                            {order.courier_metadata?.pickup_status && (
                              <div className="tw-flex tw-gap-2">
                                <span className="tw-text-gray-500">Pickup:</span>
                                <span className="tw-text-[#00C5FB] tw-capitalize">{order.courier_metadata.pickup_status}</span>
                              </div>
                            )}
                            {order.courier_metadata?.awb_code && (
                              <div className="tw-flex tw-gap-2">
                                <span className="tw-text-gray-500">AWB:</span>
                                <a 
                                  href={order.courier_metadata.tracking_url || `https://shiprocket.co/tracking/${order.courier_metadata.awb_code}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="tw-text-gray-300 tw-font-mono tw-text-[10px] hover:tw-text-[#00C5FB] hover:tw-underline tw-flex tw-items-center tw-gap-1"
                                >
                                  {order.courier_metadata.awb_code}
                                  <ExternalLink className="tw-w-2 tw-h-2" />
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="tw-text-center tw-py-20 tw-bg-[#252525] tw-rounded-3xl tw-border tw-border-dashed tw-border-gray-700">
                  <AlertCircle className="tw-w-16 tw-h-16 tw-text-gray-600 tw-mx-auto tw-mb-4" />
                  <h3 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-2">No Orders Found</h3>
                  <p className="tw-text-gray-500">We couldn't find any orders associated with "{identifier}".</p>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tw-text-center tw-py-20 tw-opacity-50"
            >
              <Package className="tw-w-24 tw-h-24 tw-mx-auto tw-mb-4 tw-text-gray-700" />
              <p className="tw-text-gray-500">Search results will appear here</p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer Decoration */}
      <div className="tw-fixed tw-bottom-0 tw-left-0 tw-w-full tw-h-1 tw-bg-gradient-to-r tw-from-transparent tw-via-[#00C5FB] tw-to-transparent tw-opacity-20" />
    </div>
  );
};

export default TrackOrders;
